/** @file Sets the curve of keys of one numeric timeline (numeric key channel or deform). */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import type { Curve } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { relativeCurve } from '../_shared/relativeCurve';
import { copyJson } from './copyJson';
import { sameJson } from './sameJson';
import { timelineKeys, type TimelineChannel } from './timelineKeys';

const isScale = (channel: TimelineChannel): boolean => channel === 'scaleX' || channel === 'scaleY';

/** animation.setCurve: the curve applies to the segment starting at each key; discrete timelines are refused. */
export const command = defineCommand({
  name: 'animation.setCurve',
  description: 'Set the curve (linear, stepped, { ease } or { bezier }) of the keys at `frames` of one numeric timeline: a numeric key channel or deform, with `target`.',
  input: z
    .object({
      animation: z.string().min(1),
      channel: timelineKeys.channel,
      target: z.string().min(1).exactOptional(),
      curve: prim.curve,
      frames: z.array(prim.frame).min(1),
    })
    .strict(),
  apply(draft, input, ctx) {
    const ref = timelineKeys.resolve(draft, input, ctx);
    if (!ref.numeric) {
      throwError('VALIDATION', undefined, { path: 'channel', hint: `${input.channel} keys are discrete; curves apply to numeric channels and deform only` });
    }
    const keys = input.frames.map(
      (f) => ref.keys.find((k) => k.f === f) ?? throwError('NOT_FOUND', { kind: 'key', ref: `${input.channel}@${f}` }, { path: 'frames', hint: 'setKey or setDeformKey creates keys' }),
    );
    // Scale keys are stored relative to the setup scale, and so are their bezier handles (m1, as in animation.setKey).
    const curve = isScale(input.channel) ? relativeCurve(draft, ctx.resolve('bone', input.target ?? ''), input.channel, input.curve) : input.curve;
    // A key without a curve is linear: setting linear there changes nothing (L-review m2); only differing keys are written.
    const same = (k: { curve?: Curve | undefined }): boolean => sameJson(k.curve ?? 'linear', curve);
    if (keys.every(same)) return;
    for (const key of keys) if (!same(key)) key.curve = copyJson(curve);
    draft.meta.updatedAt = ctx.now;
  },
});
